/**
 * Forgot Password Page with multi-step flow
 * Step 1: Enter email
 * Step 2: Enter verification code and new password
 * Step 3: Success message
 *
 * Presentación (Claude Design · Login.dc.html): esta pantalla es parte del
 * mismo flujo de acceso que /login, así que hereda su mismo shell — hero
 * fotográfico a la izquierda + tarjeta de formulario a la derecha con marcas
 * de esquina — en vez de tener una identidad visual propia. Nada de la
 * lógica de los 3 pasos (email → reset → success) cambió, solo el envoltorio.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Input, Alert } from '@components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { BrandLogo } from '@components/common/BrandLogo';
import { LoginBackgroundSlideshow } from './LoginBackgroundSlideshow';
import { cn } from '@lib/utils/cn';
import { authService } from '../services/authService';
import { TEST_IDS } from '@lib/utils/testIds';

const forgotPasswordSchema = z.object({
  email: z.string().email('Correo electrónico inválido'),
});

const resetPasswordSchema = z
  .object({
    code: z
      .string()
      .length(4, 'El código debe tener 4 dígitos')
      .regex(/^\d{4}$/, 'El código debe contener solo números'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

/** Estilos del diseño (Claude Design · Login.dc.html), mismos tokens que LoginForm.tsx. */
const FIELD_LABEL = 'text-xs font-semibold text-ink-850';
const FIELD_INPUT =
  'block w-full rounded-[10px] border border-[#ececec] bg-white px-4 py-[15px] text-[15px] font-medium leading-6 text-ink-850 ' +
  'placeholder:font-normal placeholder:text-secondary-400 ' +
  'hover:border-secondary-300 ' +
  'focus:border-secondary-400 focus:ring-4 focus:ring-secondary-400/[.14] ' +
  'transition-colors';
const FIELD_INPUT_ERROR =
  'border-danger-500 hover:border-danger-500 focus:border-danger-500 focus:ring-danger-500/20';

/** Botón primario ancho completo, mismo tratamiento que el submit de LoginForm.tsx. */
const CTA_BUTTON =
  'cta-sheen mt-2 flex w-full items-center justify-between gap-3 rounded-[10px] bg-ink-850 px-[22px] py-[17px] text-[15px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-70';

/** Botón/link secundario ancho completo, mismo tratamiento que el botón de Microsoft de LoginForm.tsx. */
const GHOST_BUTTON =
  'flex w-full items-center justify-center gap-2.5 rounded-[10px] border border-[#ececec] bg-white px-[22px] py-[15px] text-[15px] font-semibold text-ink-850 transition-colors hover:border-secondary-300 hover:bg-secondary-50';

/** Marcas de registro en las esquinas de la tarjeta (idénticas a LoginPage.tsx). */
function CornerMarks() {
  const base = 'pointer-events-none absolute h-3.5 w-3.5 border-secondary-300';
  return (
    <>
      <span aria-hidden="true" className={`${base} left-[18px] top-[18px] rounded-tl-[3px] border-l-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} right-[18px] top-[18px] rounded-tr-[3px] border-r-[1.5px] border-t-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] left-[18px] rounded-bl-[3px] border-b-[1.5px] border-l-[1.5px]`} />
      <span aria-hidden="true" className={`${base} bottom-[18px] right-[18px] rounded-br-[3px] border-b-[1.5px] border-r-[1.5px]`} />
    </>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'reset' | 'success'>('email');
  const [email, setEmail] = useState('');

  // Get tenant and tenantUrl from environment variables
  const tenant = import.meta.env.VITE_DEFAULT_TENANT || '172';
  const tenantUrl = import.meta.env.VITE_TENANT_URL || window.location.origin;

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const forgotPasswordMutation = useMutation({
    mutationFn: async (emailValue: string) => {
      // Enviar el correo de recuperación. NO se llama a getUserByEmail/
      // getConfirmEmail: ese endpoint envía un segundo correo de confirmación
      // de cuenta que confunde al usuario (bug QA).
      //
      // La respuesta ya no trae el userId — se lo queda el backend ligado al
      // correo y lo entrega solo dentro del ticket de verificación. Si el
      // correo no existe la respuesta es igual de genérica (anti-enumeración)
      // y el flujo continúa al paso del código, donde falla.
      await authService.forgotPassword(emailValue, tenant, tenantUrl, 'Cambiar contraseña');

      return { email: emailValue };
    },
    onSuccess: (data) => {
      setEmail(data.email);
      setStep('reset');
    },
  });

  // Función para reenviar el código
  const handleResendCode = async () => {
    if (!email) return;

    setIsResending(true);
    setResendSuccess(false);

    try {
      await authService.forgotPassword(email, tenant, tenantUrl, 'Cambiar contraseña');
      console.log('Code resent successfully');
      setResendSuccess(true);
      // Ocultar mensaje de éxito después de 5 segundos
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      console.error('Failed to resend code:', error);
    } finally {
      setIsResending(false);
    }
  };

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordFormData) => {
      // Validar el codigo contra mdl17/verify/check ANTES de resetear. Si el
      // codigo es incorrecto/expirado, esto lanza y resetPassword nunca se
      // llama.
      //
      // Ademas, ese paso es ahora el que autoriza el reset: devuelve un
      // `resetTicket` de un solo uso, emitido por el servidor, del que salen
      // el userId y el tenant. Encadenar los dos pasos ya no es una decision
      // de esta pantalla — sin ticket, /auth/reset-password rechaza.
      const { resetTicket } = await authService.verifyResetCode(data.code, email);

      return authService.resetPassword(resetTicket, data.password, data.confirmPassword);
    },
    onSuccess: () => {
      setStep('success');
      setTimeout(() => navigate('/login'), 3000);
    },
    onError: (error: any) => {
      console.error('Password reset error:', error);
      console.error('Error response:', error?.response?.data);
    },
  });

  const onSubmitEmail = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(data.email);
  };

  const onSubmitReset = (data: ResetPasswordFormData) => {
    resetPasswordMutation.mutate(data);
  };

  // La tarjeta del formulario topea en 520px, así que darle una fracción del
  // ancho (1fr) le regalaba ~200px muertos. Acotando esa columna a 620px, el
  // resto queda para el hero fotográfico — mismo criterio que LoginPage.tsx.
  return (
    <div className="grid min-h-screen w-full grid-cols-1 bg-white font-display lg:grid-cols-[minmax(0,1fr)_minmax(0,620px)]">
      {/* ===================== IZQUIERDA — HERO ===================== */}
      {/*
        En móvil/tablet el hero va DEBAJO del formulario, igual que en Login.
        Mismo slideshow, mismo copy de marca: recuperar contraseña es el mismo
        flujo de acceso, no una pantalla con identidad propia.
      */}
      <aside className="relative order-2 flex flex-col justify-between gap-8 overflow-hidden bg-ink-950 px-6 py-10 text-white sm:px-10 lg:order-1 lg:gap-0 lg:px-[clamp(32px,4.5vw,64px)] lg:py-[clamp(32px,4vw,56px)]">
        <LoginBackgroundSlideshow />

        {/* Grano de papel, igual que en Login. */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[.18] mix-blend-overlay"
        >
          <filter id="paper-grain-forgot-password">
            <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.35 0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#paper-grain-forgot-password)" />
        </svg>

        <div className="relative z-[2] flex items-center gap-3.5 lg:ml-4 lg:mt-5">
          <BrandLogo markSize={72} wordmarkClassName="text-[22px]" imgTestId="auth-forgot-password-page-tenant-logo-image" />
        </div>

        <div className="relative z-[2] max-w-[560px]">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/50 sm:mb-7 sm:text-[12px]">
            Facturación Electrónica · Panamá
          </div>
          <h1 className="m-0 text-[clamp(30px,2.7vw,48px)] font-bold leading-[1.05] tracking-[-0.035em] text-white">
            Tu facturación
            <br />
            electrónica,
            <br />
            <span className="font-medium text-white/[.78]">en regla con la DGI.</span>
          </h1>
          <p className="mt-6 max-w-[400px] text-base leading-[1.6] text-white/[.55]">
            Emite, consulta y archiva tus documentos fiscales sin salir del flujo de trabajo.
          </p>
        </div>

        <div className="relative z-[2] flex flex-col gap-1 text-[12px] text-white/[.35] sm:flex-row sm:items-center sm:justify-between sm:text-xs">
          <span>
            © {new Date().getFullYear()} Facturación Electrónica. Todos los derechos reservados.
          </span>
          <span className="font-mono tracking-[0.08em]">v{__APP_VERSION__}</span>
        </div>
      </aside>

      {/* ===================== DERECHA — RECUPERAR CONTRASEÑA ===================== */}
      <main className="order-1 flex items-center justify-center overflow-y-auto bg-secondary-100 p-4 sm:p-6 lg:order-2 lg:p-10 short:lg:p-6">
        <div className="relative w-full max-w-[520px] rounded-3xl border border-[#f4f4f4] bg-white px-6 pb-11 pt-12 shadow-[0_1px_1px_rgba(10,34,51,.02),0_30px_60px_-30px_rgba(10,34,51,.08)] sm:px-14 sm:pt-[60px] short:pb-7 short:pt-8 short:sm:pt-9">
          <CornerMarks />

          <div className="mb-5 text-[12px] font-semibold uppercase tracking-[0.28em] text-secondary-400 short:mb-2">
            Recuperación de acceso
          </div>

          {step === 'email' && (
            <>
              <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
                Recuperar Contraseña
              </h2>
              <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
                Ingresa tu correo electrónico y te enviaremos un código de verificación.
              </p>

              <form onSubmit={handleSubmitEmail(onSubmitEmail)} className="space-y-6 short:space-y-4" data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.FORM}>
                {forgotPasswordMutation.error && (
                  <Alert variant="danger" title="Error">
                    {(() => {
                      const error = forgotPasswordMutation.error as any;
                      const errorMessage = error?.response?.data?.message || error?.message;

                      // Nota: no se distingue "correo no registrado" a propósito
                      // (anti-enumeración). Solo se reportan errores de conexión reales.
                      if (errorMessage?.includes('Network Error')) {
                        return 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
                      }

                      return 'Ocurrió un error al enviar el código. Por favor, intenta de nuevo.';
                    })()}
                  </Alert>
                )}

                <div className="flex flex-col gap-2">
                  <label htmlFor="forgot-password-email" className={FIELD_LABEL}>
                    Correo Electrónico
                  </label>
                  <Input
                    id="forgot-password-email"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    error={errorsEmail.email?.message}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.EMAIL_INPUT}
                    className={cn(FIELD_INPUT, errorsEmail.email && FIELD_INPUT_ERROR)}
                    {...registerEmail('email')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={forgotPasswordMutation.isPending}
                  className={CTA_BUTTON}
                  data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.SUBMIT_BUTTON}
                >
                  <span>{forgotPasswordMutation.isPending ? 'Enviando…' : 'Enviar Código de Verificación'}</span>
                  {forgotPasswordMutation.isPending ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <svg className="cta-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  )}
                </button>

                <Link to="/login" className={GHOST_BUTTON}>
                  <ArrowLeftIcon className="h-4 w-4" />
                  Volver al Login
                </Link>
              </form>
            </>
          )}

          {step === 'reset' && (
            <>
              <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
                Crear Nueva Contraseña
              </h2>
              <p className="mb-10 mt-3 text-[15px] leading-[1.55] text-secondary-500 short:mb-5 short:mt-1.5">
                Ingresa el código que enviamos a tu correo y tu nueva contraseña.
              </p>

              <form onSubmit={handleSubmitReset(onSubmitReset)} className="space-y-6 short:space-y-4">
                {resetPasswordMutation.error && (
                  <Alert variant="danger" title="Error al Resetear Contraseña">
                    {(() => {
                      const error = resetPasswordMutation.error as any;
                      const errorMessage = error?.response?.data?.message || error?.message;
                      if (errorMessage) {
                        return `Error: ${errorMessage}`;
                      }
                      return 'El código es incorrecto o ha expirado. Por favor, verifica el código enviado a tu correo o solicita uno nuevo.';
                    })()}
                  </Alert>
                )}

                <Alert variant="info" title="Código de Verificación">
                  <p>
                    Hemos enviado un código de 4 dígitos a tu correo electrónico:{' '}
                    <strong>{email}</strong>
                  </p>
                  <p className="mt-2 text-xs text-secondary-500">
                    Si no lo recibes, revisa tu carpeta de spam o{' '}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isResending}
                      className="font-semibold text-teal-500 transition-colors hover:underline disabled:opacity-50"
                    >
                      {isResending ? 'reenviando...' : 'reenviar código'}
                    </button>
                  </p>
                </Alert>

                {resendSuccess && (
                  <Alert variant="success" title="Código Reenviado">
                    Se ha reenviado el código de verificación a tu correo.
                  </Alert>
                )}

                <div className="flex flex-col gap-2">
                  <label htmlFor="forgot-password-code" className={FIELD_LABEL}>
                    Código de Verificación
                  </label>
                  <Input
                    id="forgot-password-code"
                    type="text"
                    placeholder="1234"
                    maxLength={4}
                    error={errorsReset.code?.message}
                    helperText="Ingresa el código de 4 dígitos enviado a tu correo"
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.CODE_INPUT}
                    className={cn(FIELD_INPUT, 'tracking-[0.3em]', errorsReset.code && FIELD_INPUT_ERROR)}
                    {...registerReset('code')}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="forgot-password-password" className={FIELD_LABEL}>
                    Nueva Contraseña
                  </label>
                  <Input
                    id="forgot-password-password"
                    type="password"
                    placeholder="••••••••"
                    error={errorsReset.password?.message}
                    helperText="Mínimo 8 caracteres"
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.PASSWORD_INPUT}
                    className={cn(FIELD_INPUT, 'tracking-[0.1em]', errorsReset.password && FIELD_INPUT_ERROR)}
                    {...registerReset('password')}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="forgot-password-confirm-password" className={FIELD_LABEL}>
                    Confirmar Contraseña
                  </label>
                  <Input
                    id="forgot-password-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    error={errorsReset.confirmPassword?.message}
                    data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.CONFIRM_PASSWORD_INPUT}
                    className={cn(FIELD_INPUT, 'tracking-[0.1em]', errorsReset.confirmPassword && FIELD_INPUT_ERROR)}
                    {...registerReset('confirmPassword')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={resetPasswordMutation.isPending}
                  className={CTA_BUTTON}
                  data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.RESET_SUBMIT_BUTTON}
                >
                  <span>{resetPasswordMutation.isPending ? 'Actualizando…' : 'Actualizar Contraseña'}</span>
                  {resetPasswordMutation.isPending ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  ) : (
                    <svg className="cta-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className={GHOST_BUTTON}
                  data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.BACK_BUTTON}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Volver
                </button>
              </form>
            </>
          )}

          {step === 'success' && (
            <>
              <h2 className="m-0 text-4xl font-bold leading-[1.1] tracking-[-0.03em] text-ink-850 short:text-3xl">
                ¡Contraseña Actualizada!
              </h2>
              <div className="mt-6">
                <Alert variant="success" title="Éxito" data-testid={TEST_IDS.AUTH.FORGOT_PASSWORD.SUCCESS_ALERT}>
                  Tu contraseña ha sido actualizada exitosamente. Redirigiendo al login...
                </Alert>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
